/* eslint-disable react/prop-types */
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "contexts/AuthContext";
import useAlerts from "hooks/useAlerts";

import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import Icon from "@mui/material/Icon";
import OutlinedInput from "@mui/material/OutlinedInput";
import Avatar from "@mui/material/Avatar";
import Badge from "@mui/material/Badge";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Divider from "@mui/material/Divider";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material/styles";

const SEVERITY_ICON = { error: "error", warning: "warning_amber", info: "info" };
const SEVERITY_COLOR = { error: "error.main", warning: "warning.main", info: "info.main" };

/**
 * Top bar — white background, hairline border, search + notifications + user menu.
 */
export default function TopBar({ onMenuClick }) {
  const theme = useTheme();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [anchor, setAnchor] = useState(null);
  const [notifAnchor, setNotifAnchor] = useState(null);
  const { alerts, count } = useAlerts();

  return (
    <Box
      sx={{
        position: "sticky",
        top: 0,
        zIndex: 10,
        display: "flex",
        alignItems: "center",
        gap: 2,
        px: { xs: 2, md: 3 },
        height: 64,
        bgcolor: "background.paper",
        borderBottom: `1px solid ${theme.palette.divider}`,
      }}
    >
      <IconButton onClick={onMenuClick} sx={{ display: { xs: "inline-flex", md: "none" } }}>
        <Icon>menu</Icon>
      </IconButton>

      {/* Search */}
      <OutlinedInput
        size="small"
        placeholder="Search members, payments, attendance…"
        startAdornment={
          <InputAdornment position="start">
            <Icon sx={{ color: "text.secondary", fontSize: 20 }}>search</Icon>
          </InputAdornment>
        }
        sx={{
          flex: 1,
          maxWidth: 480,
          bgcolor: theme.palette.ink[100],
          "& .MuiOutlinedInput-notchedOutline": { borderColor: "transparent" },
          "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: theme.palette.ink[200] },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: theme.palette.primary.main,
          },
        }}
      />

      <Box sx={{ flex: 1 }} />

      {/* Notifications */}
      <IconButton sx={{ color: "text.secondary" }} onClick={(e) => setNotifAnchor(e.currentTarget)}>
        <Badge badgeContent={count} color="error" max={9}>
          <Icon>notifications_none</Icon>
        </Badge>
      </IconButton>

      <Menu
        anchorEl={notifAnchor}
        open={Boolean(notifAnchor)}
        onClose={() => setNotifAnchor(null)}
        PaperProps={{
          sx: { mt: 1, width: 360, maxHeight: 420, borderRadius: 2, boxShadow: 6 },
        }}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      >
        <Box sx={{ px: 2, py: 1.5 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            Notifications
          </Typography>
        </Box>
        <Divider />
        {alerts.length === 0 && (
          <Box sx={{ px: 2, py: 3, textAlign: "center" }}>
            <Typography variant="body2" color="text.secondary">
              You&apos;re all caught up.
            </Typography>
          </Box>
        )}
        {alerts.map((alert) => (
          <MenuItem
            key={alert.id}
            onClick={() => {
              setNotifAnchor(null);
              navigate(alert.link);
            }}
            sx={{ whiteSpace: "normal", alignItems: "flex-start", py: 1.25 }}
          >
            <ListItemIcon sx={{ mt: 0.25 }}>
              <Icon fontSize="small" sx={{ color: SEVERITY_COLOR[alert.severity] }}>
                {SEVERITY_ICON[alert.severity] || "info"}
              </Icon>
            </ListItemIcon>
            <ListItemText
              primary={alert.title}
              secondary={alert.message}
              primaryTypographyProps={{ variant: "body2", fontWeight: 600 }}
              secondaryTypographyProps={{ variant: "caption" }}
            />
          </MenuItem>
        ))}
      </Menu>

      {/* User menu */}
      <Box
        onClick={(e) => setAnchor(e.currentTarget)}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          pl: 1,
          pr: 1.5,
          py: 0.5,
          borderRadius: 2,
          cursor: "pointer",
          "&:hover": { bgcolor: theme.palette.ink[100] },
        }}
      >
        <Avatar
          sx={{
            width: 32,
            height: 32,
            bgcolor: theme.palette.teal[100],
            color: theme.palette.teal[700],
            fontWeight: 700,
            fontSize: 13,
          }}
        >
          {(user?.name?.[0] || "U").toUpperCase()}
        </Avatar>
        <Box sx={{ display: { xs: "none", sm: "block" }, lineHeight: 1.1 }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {firstName(user?.name)}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {humanRole(user?.role)}
          </Typography>
        </Box>
        <Icon sx={{ color: "text.secondary", fontSize: 20 }}>expand_more</Icon>
      </Box>

      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        PaperProps={{
          sx: { mt: 1, minWidth: 200, borderRadius: 2, boxShadow: 6 },
        }}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      >
        <MenuItem
          onClick={() => {
            setAnchor(null);
            navigate("/profile");
          }}
        >
          <ListItemIcon>
            <Icon fontSize="small">person</Icon>
          </ListItemIcon>
          My profile
        </MenuItem>
        <Divider />
        <MenuItem
          onClick={() => {
            setAnchor(null);
            logout();
          }}
          sx={{ color: "error.main" }}
        >
          <ListItemIcon>
            <Icon fontSize="small" sx={{ color: "error.main" }}>
              logout
            </Icon>
          </ListItemIcon>
          Sign out
        </MenuItem>
      </Menu>
    </Box>
  );
}

function firstName(name) {
  return (name || "Member").split(" ")[0];
}

function humanRole(role) {
  if (!role) return "Member";
  return role.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}
